// src/components/calculators/QuickFeeCheck.js
import React from 'react';
import { 
  ArrowLeftIcon, 
  CurrencyPoundIcon,
  InformationCircleIcon,
  CalculatorIcon
} from "@heroicons/react/24/outline";
import { useCalculator } from './CalculatorContext';
import ResultsDisplay from './ResultsDisplay';
import VATToggle from './VATToggle';

const QuickFeeCheck = ({ onBack }) => {
  const {
    quickCheckData,
    setQuickCheckData,
    quickCheckResults,
    validateInput,
    formatCurrency,
    formatPercentage
  } = useCalculator();

  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (validateInput(value) || value === '') {
      setQuickCheckData(prev => ({ ...prev, sellingPrice: value }));
    }
  };

  const handleClearAll = () => {
    setQuickCheckData({
      sellingPrice: '',
      vatRate: '20',
      applyVAT: false
    });
  };

  const styles = {
    container: {
      fontFamily: 'var(--font-family-primary)'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'var(--spacing-xs)',
      padding: 'var(--spacing-xs) var(--spacing-m)',
      background: 'var(--Button-Primary-Enabled-BG)',
      color: 'var(--Button-Primary-Text)',
      border: 'none',
      borderRadius: 'var(--button-radius)',
      fontSize: 'var(--font-size-button)',
      fontWeight: 'var(--button-font-weight)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: 'var(--spacing-l)'
    },
    // FIX: Removed the headerBanner style and updated the title style below
    title: {
      fontSize: 'var(--font-size-h4)',
      fontWeight: 'var(--font-weight-bold)',
      color: 'var(--Text-Primary)', // Changed from --Text-Contrast
      marginBottom: 'var(--spacing-xl)' // Added margin
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(1, 1fr)',
      gap: 'var(--spacing-xl)',
      '@media (min-width: 1024px)': {
        gridTemplateColumns: 'repeat(2, 1fr)'
      }
    },
    card: {
      background: 'var(--Surface-Primary)',
      borderRadius: 'var(--radius-m)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid var(--Stroke-Light)',
      overflow: 'hidden'
    },
    cardHeader: {
      background: 'var(--Blue-500)',
      padding: 'var(--spacing-m)',
      borderBottom: '1px solid var(--Purple-100)'
    },
    cardTitle: {
      fontSize: 'var(--font-size-body2)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--Text-Contrast)',
      margin: 0
    },
    cardBody: {
      padding: 'var(--spacing-l)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--spacing-l)'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--Text-Primary)',
      marginBottom: 'var(--spacing-xs)'
    },
    inputWrapper: {
      position: 'relative'
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-xs) var(--spacing-s)',
      paddingLeft: 'calc(var(--spacing-s) * 2.5)',
      border: 'var(--input-border)',
      borderRadius: 'var(--input-radius)',
      fontSize: 'var(--font-size-body3)',
      color: 'var(--Text-Primary)',
      background: 'var(--Surface-Primary)',
      transition: 'all 0.2s ease',
      fontFamily: 'var(--font-family-primary)'
    },
    inputIcon: {
      position: 'absolute',
      left: 'var(--spacing-s)',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '20px',
      height: '20px',
      color: 'var(--Text-Tertiary)'
    },
    helperText: {
      fontSize: 'var(--font-size-body5)',
      color: 'var(--Text-Tertiary)',
      marginTop: 'var(--spacing-xxs)'
    },
    clearButton: {
      background: 'transparent',
      color: 'var(--Red-600)',
      border: 'none',
      padding: 'var(--spacing-xs)',
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
      fontFamily: 'var(--font-family-primary)'
    },
    infoCard: {
      background: 'var(--brand-gradient-3)',
      border: '1px solid var(--Blue-200)',
      borderRadius: 'var(--radius-m)',
      padding: 'var(--spacing-m)',
      marginTop: 'var(--spacing-m)',
      display: 'flex',
      gap: 'var(--spacing-s)'
    },
    infoIcon: {
      width: '20px',
      height: '20px',
      color: 'var(--Grey-700)',
      flexShrink: 0,
      marginTop: '2px'
    },
    infoTitle: {
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--font-weight-bold)',
      color: 'var(--Grey-800)',
      marginBottom: 'var(--spacing-xxs)'
    },
    infoText: {
      fontSize: 'var(--font-size-body4)',
      color: 'var(--Grey-800)',
      lineHeight: 'var(--line-height-normal)'
    },
    emptyState: {
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px dashed var(--Grey-300)',
      borderRadius: 'var(--radius-m)',
      background: 'var(--Surface-Primary)'
    },
    emptyIcon: {
      width: '48px',
      height: '48px',
      color: 'var(--Grey-400)',
      marginBottom: 'var(--spacing-m)'
    },
    emptyTitle: {
      fontSize: 'var(--font-size-body2)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--Text-Secondary)',
      marginBottom: 'var(--spacing-xs)'
    },
    emptyText: {
      fontSize: 'var(--font-size-body4)',
      color: 'var(--Text-Tertiary)'
    }
  };

  return (
    <div style={styles.container}>
      {/* Back Button */}
      <button
        style={styles.backButton}
        onClick={onBack}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--Button-Primary-Hovered-BG)';
          e.currentTarget.style.gap = 'var(--spacing-s)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'var(--Button-Primary-Enabled-BG)';
          e.currentTarget.style.gap = 'var(--spacing-xs)';
        }}
      >
        <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
        Back
      </button>

      {/* FIX: Header is now a standalone heading */}
      <h3 style={styles.title}>Quick Fee Check</h3>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: 'var(--spacing-xl)'
      }}>
        {/* Input Section */}
        <div>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h6 style={styles.cardTitle}>Enter Selling Price</h6>
            </div>
            <div style={styles.cardBody}>
              {/* Price Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Customer Price (What they'll pay)
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    value={quickCheckData.sellingPrice}
                    onChange={handlePriceChange}
                    placeholder="0.00"
                    style={styles.input}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = 'var(--Input-Border-Focused)';
                      e.currentTarget.style.boxShadow = 'var(--Input-Focus-Ring)';
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = 'var(--Input-Border-Default)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  <CurrencyPoundIcon style={styles.inputIcon} />
                </div>
                <p style={styles.helperText}>
                  Enter the total amount the customer will pay
                </p>
              </div>

              {/* VAT Toggle */}
              <VATToggle
                applyVAT={quickCheckData.applyVAT}
                vatRate={quickCheckData.vatRate}
                onChange={(field, value) => 
                  setQuickCheckData(prev => ({ ...prev, [field]: value }))
                }
              />

              {/* Clear Button */}
              <button
                style={styles.clearButton}
                onClick={handleClearAll}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--Red-700)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--Red-600)'}
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Info Card */}
          <div style={styles.infoCard}>
            <InformationCircleIcon style={styles.infoIcon} />
            <div>
              <p style={styles.infoTitle}>
                How fees are calculated
              </p>
              <p style={styles.infoText}>
                Boutee uses a tiered commission structure that decreases as your sales increase. 
                Card processing fees are a flat 1.8% of the total transaction.
              </p>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div>
          {quickCheckResults ? (
            <ResultsDisplay 
              results={quickCheckResults}
              mode="quickCheck"
            />
          ) : (
            <div style={styles.emptyState}>
              <div style={{ textAlign: 'center', padding: 'var(--spacing-l)' }}>
                <div style={{
                  padding: 'var(--spacing-m)',
                  background: 'var(--Grey-100)',
                  borderRadius: 'var(--radius-pill)',
                  display: 'inline-block',
                  marginBottom: 'var(--spacing-m)'
                }}>
                  <CalculatorIcon style={styles.emptyIcon} />
                </div>
                <h6 style={styles.emptyTitle}>
                  No calculation yet
                </h6>
                <p style={styles.emptyText}>
                  Enter a selling price to see your fee breakdown
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickFeeCheck;